/**
 * Build `files` payload for addToCart from a file input element.
 * Output shape matches what appendFiles() accepts.
 *
 * @param {HTMLInputElement | null | undefined} fileInput
 * @param {{ defaultLabel?: string }} [options]
 * @returns {Record<string, File>}
 *
 * @example
 * const filesPayload = buildFilesPayloadFromInput(document.getElementById('planFile'));
 * await addToCart({ id: 123, quantity: 1, properties: { foo: 'bar' }, files: filesPayload });
 */
function buildFilesPayloadFromInput(fileInput, options = {}) {
  if (!(fileInput instanceof HTMLInputElement) || fileInput.type !== 'file') {
    return {};
  }

  const { defaultLabel = 'Plan' } = options;
  const files = Array.from(fileInput.files || []);
  if (files.length === 0) return {};

  /** @type {Record<string, File>} */
  const payload = {};

  if (files.length === 1) {
    const singleFile = files[0];
    if (singleFile) {
      payload[defaultLabel] = singleFile;
    }
    return payload;
  }

  const usedLabels = new Set();
  files.forEach((currentFile) => {
    const baseLabel = (currentFile.name || defaultLabel).replace(/[\[\]]/g, '').trim() || defaultLabel;
    let label = baseLabel;
    let copyIndex = 2;
    while (usedLabels.has(label)) {
      label = `${baseLabel} (${copyIndex})`;
      copyIndex += 1;
    }
    usedLabels.add(label);
    payload[label] = currentFile;
  });

  return payload;
}

/**
 * @param {FormData} formData
 * @param {string} key
 * @param {unknown} value
 */
function appendProperty(formData, key, value) {
  if (!key) return;
  if (value === null || value === undefined || value === '') return;
  formData.append(`properties[${String(key)}]`, String(value));
}

/**
 * @param {FormData} formData
 * @param {unknown} filesInput
 */
function appendFiles(formData, filesInput) {
  if (!filesInput) return;

  // Array<File> => default "Plan" label behavior
  if (Array.isArray(filesInput)) {
    if (filesInput.length === 1) {
      const file = filesInput[0];
      if (file instanceof File) formData.append('properties[Plan]', file);
      return;
    }

    const usedLabels = new Set();
    filesInput.forEach((file) => {
      if (!(file instanceof File)) return;
      const baseLabel = (file.name || 'Plan').replace(/[\[\]]/g, '').trim() || 'Plan';
      let label = baseLabel;
      let copyIndex = 2;
      while (usedLabels.has(label)) {
        label = `${baseLabel} (${copyIndex})`;
        copyIndex += 1;
      }
      usedLabels.add(label);
      formData.append(`properties[${label}]`, file);
    });
    return;
  }

  // Object<string, File | File[]>
  Object.entries(filesInput).forEach(([label, value]) => {
    if (value instanceof File) {
      formData.append(`properties[${label}]`, value);
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((file, index) => {
        if (file instanceof File) {
          const nextLabel = value.length > 1 ? `${label} ${index + 1}` : label;
          formData.append(`properties[${nextLabel}]`, file);
        }
      });
    }
  });
}

function getCartRoot() {
  return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
}

/**
 * @param {FormData} formData
 * @returns {Promise<object>}
 */
async function postCartFormData(formData) {
  const root = getCartRoot();
  const response = await fetch(root + 'cart/add.js', {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body: formData,
  });

  const raw = await response.text();
  let data = null;
  try {
    data = raw ? JSON.parse(raw) : null;
  } catch (_error) {
    throw new Error(`Response is not valid JSON (status ${response.status}). Raw response length: ${raw.length}`);
  }

  if (!response.ok) {
    throw new Error(data?.description || data?.message || `Failed to add to cart (HTTP ${response.status})`);
  }

  return data;
}

/**
 * @param {CartQueueItem | null | undefined} item
 * @returns {FormData}
 */
function buildCartFormData(item) {
  if (item?.formData instanceof FormData) {
    return item.formData;
  }

  if (!item || !item.id) {
    throw new Error('Cart item "id" is required');
  }

  const formData = new FormData();
  formData.set('id', String(item.id));
  formData.set('quantity', String(item.quantity || 1));

  let properties = item.properties || {};
  if (Array.isArray(item.readableItems) && item.readableItems.length > 0) {
    properties = {
      ...properties,
      ...buildReadableLineItemProperties(item.readableItems, item.readableOptions || {}),
    };
  }
  Object.entries(properties).forEach(([key, value]) => appendProperty(formData, key, value));
  appendFiles(formData, item.files);

  return formData;
}

/**
 * Adds a single line item directly (supports files as line_item properties). Only use to add a single item, not multiple ones.
 *
 * @param {CartQueueItem | null | undefined} item
 * @returns {Promise<object>}
 */
async function addToCart(item) {
  const formData = buildCartFormData(item);
  const data = await postCartFormData(formData);
  window.dispatchEvent(new CustomEvent('cart:updated'));
  return data;
}

/**
 * Remove null-ish/empty props and stringify values (Shopify stores properties as strings).
 * @param {Record<string, any> | null | undefined} properties
 */
function normalizeProperties(properties) {
  if (!properties || typeof properties !== 'object') return undefined;

  const out = {};
  for (const [k, v] of Object.entries(properties)) {
    if (!k) continue;
    if (v === null || v === undefined || v === '') continue;
    out[String(k)] = String(v);
  }
  return Object.keys(out).length ? out : undefined;
}

/**
 * @param {any} item
 */
function normalizeMultiAddItem(item) {
  if (!item || !item.id) throw new Error('Each item must have an id (variant id).');

  if (item.files) {
    throw new Error('Multi-add does not support files. Use the single addToCart() for file uploads.');
  }

  const id = (typeof item.id === 'number') ? item.id : parseInt(String(item.id), 10);
  if (!Number.isFinite(id)) throw new Error(`Invalid variant id: ${item.id}`);

  const quantity = item.quantity == null ? 1 : parseInt(String(item.quantity), 10);
  if (!Number.isFinite(quantity) || quantity <= 0) {
    throw new Error(`Invalid quantity for variant ${item.id}: ${item.quantity}`);
  }

  const normalized = {
    id,
    quantity,
  };

  const props = normalizeProperties(item.properties);
  if (props) normalized.properties = props;

  // Optional, if you ever need subscriptions / selling plans
  if (item.selling_plan != null && item.selling_plan !== '') {
    const sp = (typeof item.selling_plan === 'number') ? item.selling_plan : parseInt(String(item.selling_plan), 10);
    if (Number.isFinite(sp)) normalized.selling_plan = sp;
  }

  return normalized;
}

function getCartRoot() {
  return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
}

/**
 * Add multiple items to cart in ONE request (no files).
 *
 * @param {Array<{id:number|string, quantity?:number|string, properties?:Record<string, any>, selling_plan?:number|string}>} items
 * }} [options]
 *
 * @returns {Promise<any>} Shopify returns a JSON object containing line items associated with the added items.
 */
async function addManyToCart(items, options = {}) {
  const { dispatchEvent = true } = options;

  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }

  if (items.length > 30) {
    // You said you’ll be under 25, but guard anyway.
    throw new Error(`Too many items (${items.length}). Expected <= 30.`);
  }

  const root = getCartRoot();
  const payload = {
    items: items.map(normalizeMultiAddItem),
  };

  const response = await fetch(root + 'cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });

  const raw = await response.text();
  let data = null;

  try {
    data = raw ? JSON.parse(raw) : null;
  } catch (_error) {
    throw new Error(`Response is not valid JSON (status ${response.status}). Raw response length: ${raw.length}`);
  }

  if (!response.ok) {
    // Shopify commonly returns 422 with { status, message, description }
    throw new Error(data?.description || data?.message || `Failed to add items to cart (HTTP ${response.status})`);
  }

  if (dispatchEvent) {
    window.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'addManyToCart', data } }));
  }

  return data;
}

/**
 * Apply a discount code to the current cart, preserving any codes already applied.
 * Uses the same Cart Ajax API as the cart discount form (`cart/update.js`).
 *
 * @param {string} code
 * @returns {Promise<object|null>} Updated cart, or the current cart if the code was already applied.
 */
async function applyDiscountCode(code) {
  const discountCode = String(code || '').trim();
  if (!discountCode) return null;

  const root = getCartRoot();
  const cartResponse = await fetch(root + 'cart.js', {
    headers: { Accept: 'application/json' },
  });

  const rawCart = await cartResponse.text();
  let cart = null;
  try {
    cart = rawCart ? JSON.parse(rawCart) : null;
  } catch (_error) {
    throw new Error(`Response is not valid JSON (status ${cartResponse.status}).`);
  }

  if (!cartResponse.ok) {
    throw new Error(cart?.description || cart?.message || `Failed to load cart (HTTP ${cartResponse.status})`);
  }

  const existing = Array.isArray(cart?.discount_codes)
    ? cart.discount_codes
        .map((entry) => (entry && entry.code ? String(entry.code) : ''))
        .filter(Boolean)
    : [];

  if (existing.some((existingCode) => existingCode.toLowerCase() === discountCode.toLowerCase())) {
    return cart;
  }

  const response = await fetch(root + 'cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ discount: [...existing, discountCode].join(',') }),
  });

  const raw = await response.text();
  let data = null;
  try {
    data = raw ? JSON.parse(raw) : null;
  } catch (_error) {
    throw new Error(`Response is not valid JSON (status ${response.status}). Raw response length: ${raw.length}`);
  }

  if (!response.ok) {
    throw new Error(data?.description || data?.message || `Failed to apply discount (HTTP ${response.status})`);
  }

  return data;
}

/**
 * Empties the cart. Uses Shopify's cart/clear.js endpoint.
 *
 * @returns {Promise<object>} Shopify cart object (empty items array).
 */
async function nukeCart() {
  const root = getCartRoot();
  const response = await fetch(root + 'cart/clear.js', {
    method: 'POST',
    headers: { Accept: 'application/json' },
  });

  const raw = await response.text();
  let data = null;
  try {
    data = raw ? JSON.parse(raw) : null;
  } catch (_error) {
    throw new Error(`Response is not valid JSON (status ${response.status}). Raw response length: ${raw.length}`);
  }

  if (!response.ok) {
    throw new Error(data?.description || data?.message || `Failed to clear cart (HTTP ${response.status})`);
  }

  window.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'emptyCart', data } }));
  window.location.reload();
  return data;
}

async function removeBundleItems(bundle_id) {
  const bundle_items = document.querySelectorAll(`tr[data-bundle-id="${bundle_id}"]`);

  const updates = {};
  bundle_items.forEach((item) => {
    const key = item.dataset.key;
    if (key) updates[key] = 0;
  });

  if (Object.keys(updates).length === 0) return;  

  const res = await fetch("/cart/update.js", {
    method: "POST",
    headers: { "Content-Type": "application/json", "Accept": "application/json" },
    body: JSON.stringify({ updates }),
  });

  if (!res.ok) {
    console.error("Bundle remove failed", await res.text());
    return;
  }

  window.location.reload();
}

async function updateCartCount() {
  const cartCount = document.querySelector('[data-cart-count]');
  if (!cartCount) return;

  try {
    const response = await fetch('/cart.js');
    const cartData = await response.json();
    cartCount.textContent = cartData.item_count ?? 0;
  } catch (error) {
    console.error('Error fetching cart details:', error);
  }
}

// Make functions available globally
if (typeof window !== 'undefined') {
  window.buildFilesPayloadFromInput = buildFilesPayloadFromInput;
  window.addToCart = addToCart;
  window.addManyToCart = addManyToCart;
  window.applyDiscountCode = applyDiscountCode;
  window.nukeCart = nukeCart;
  window.updateCartCount = updateCartCount;
  window.removeBundleItems = removeBundleItems;
}